import { createServerFn } from '@tanstack/react-start'
import { getRequestHeader, getRequestIP } from '@tanstack/react-start/server'
import { z } from 'zod'

const ADMIN_EMAIL = 'nkadimengdanny7@gmail.com'

const EventSchema = z.object({
  eventType: z.enum([
    'admin_signin_failed',
    'admin_signin_success',
    'admin_password_changed',
    'client_signin_failed_burst',
    'password_reset_requested',
  ]),
  email: z.string().email().optional(),
  metadata: z.record(z.any()).optional(),
})

/**
 * Public, unauthenticated endpoint so failed sign-in attempts (where no
 * session exists) can still be reported. Rate limited by (event_type, ip)
 * in the last 5 minutes to avoid alert fatigue / email flooding.
 */
export const recordAuthSecurityEvent = createServerFn({ method: 'POST' })
  .inputValidator((raw: unknown) => EventSchema.parse(raw))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import('@/integrations/supabase/client.server')
    const { sendSystemEmail } = await import('@/lib/email/system-send.server')

    // Trusted, edge-set client IP only. Never use the client-suppliable
    // X-Forwarded-For chain, which can be spoofed to bypass dedupe/rate limits.
    const ip = (() => {
      try {
        return (
          getRequestHeader('cf-connecting-ip') ??
          getRequestHeader('x-real-ip') ??
          getRequestIP() ??
          null
        )
      } catch {
        return null
      }
    })()
    const userAgent = (() => {
      try {
        return getRequestHeader('user-agent') ?? null
      } catch {
        return null
      }
    })()

    const severity =
      data.eventType === 'admin_signin_failed' || data.eventType === 'client_signin_failed_burst'
        ? 'warning'
        : data.eventType === 'admin_signin_success'
        ? 'info'
        : 'critical'

    // Dedupe: don't alert on the same (event, ip) within 5 minutes
    const fiveMinAgo = new Date(Date.now() - 5 * 60_000).toISOString()
    const { data: recent } = await supabaseAdmin
      .from('security_alerts')
      .select('id, notified_admins')
      .eq('event_type', data.eventType)
      .eq('ip', ip ?? '')
      .gte('created_at', fiveMinAgo)
      .limit(1)
    const alreadyNotified = Array.isArray(recent) && recent.length > 0 && recent[0].notified_admins

    const { data: inserted, error } = await supabaseAdmin
      .from('security_alerts')
      .insert({
        event_type: data.eventType,
        severity,
        email: data.email ?? null,
        ip,
        user_agent: userAgent,
        metadata: data.metadata ?? {},
      })
      .select('id')
      .single()
    if (error) {
      console.error('[security-alerts] insert failed', error)
      return { ok: false }
    }

    // Only email admin for high-signal events, and skip if we've already
    // notified for the same event+ip in the last 5 minutes.
    const emailable = new Set([
      'admin_signin_failed',
      'admin_signin_success',
      'admin_password_changed',
      'client_signin_failed_burst',
    ])
    // Absolute cap: never send more than 10 alert emails per hour, regardless
    // of event type or IP, so a spoofing attacker cannot flood the inbox.
    const oneHourAgo = new Date(Date.now() - 60 * 60_000).toISOString()
    const { count: notifiedLastHour } = await supabaseAdmin
      .from('security_alerts')
      .select('id', { count: 'exact', head: true })
      .eq('notified_admins', true)
      .gte('created_at', oneHourAgo)
    const underHourlyCap = (notifiedLastHour ?? 0) < 10

    if (!alreadyNotified && underHourlyCap && emailable.has(data.eventType)) {
      const occurredAt = new Date().toLocaleString('en-ZA', {
        timeZone: 'Africa/Johannesburg',
        dateStyle: 'medium',
        timeStyle: 'short',
      })
      const summaryMap: Record<string, string> = {
        admin_signin_failed:
          'A failed admin sign-in attempt was detected. If this was not you, rotate the admin password immediately.',
        admin_signin_success:
          'A successful admin sign-in occurred. If you did not sign in from this device, treat it as a compromise.',
        admin_password_changed:
          'The admin password was just changed. If you did not do this, contact support and reset immediately.',
        client_signin_failed_burst:
          'Multiple failed sign-in attempts on a client account were detected in a short window.',
      }
      await sendSystemEmail({
        templateName: 'security-alert',
        recipientEmail: ADMIN_EMAIL,
        idempotencyKey: `sec-alert-${inserted!.id}`,
        templateData: {
          eventType: data.eventType,
          severity,
          email: data.email ?? '',
          ip: ip ?? '',
          userAgent: userAgent ?? '',
          occurredAt,
          summary: summaryMap[data.eventType],
          reviewUrl: 'https://nkadimengdannylcs.com/portal/admin/security-alerts',
        },
        metadata: { alert_id: inserted!.id, event_type: data.eventType },
      })
      await supabaseAdmin
        .from('security_alerts')
        .update({ notified_admins: true })
        .eq('id', inserted!.id)
    }

    return { ok: true, id: inserted?.id }
  })
